<script setup>
/**
 * FeaturePopup
 *
 * Reusable popup component for displaying feature details.
 * Can be used on maps, in lists, or anywhere feature info is needed.
 *
 * Based on LayerCollectedDataPopup but made more generic and reusable.
 */
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import MapLayerPopup from '@/components/map/MapLayerPopup.vue'
import { formatTime } from '@/utils/datetime'
import { area } from '@turf/turf'
import { hasAreaGeometry } from '@/utils/geometry'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, Download } from 'lucide-vue-next'
import { MediaList } from '@/components/media'
import FeatureThread from '@/components/discussion/FeatureThread.vue'
import { HOST, API } from '@/utils/http'

const router = useRouter()
const store = useStore()

const props = defineProps({
  // Map instance (optional - for positioning on map)
  map: {
    type: Object,
    default: null
  },
  // Visibility state
  isVisible: {
    type: Boolean,
    required: true
  },
  // Coordinates for popup positioning (optional - for map display)
  coordinates: {
    type: Array,
    default: null
  },
  // The feature to display
  feature: {
    type: Object,
    default: null
  },
  // Counter to force re-fetch on same feature
  counter: {
    type: Number,
    default: 0
  },
  // User settings (for timezone, etc.)
  settings: {
    type: Object,
    default: () => ({ tz: 'UTC' })
  },
  // Identifier code (for keying)
  code: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close'])

const isLoadingFeatureDetails = ref(false)
const featureDetails = ref(null)
const projectData = ref(null)
const isLoadingProjectData = ref(false)

/**
 * Fetch full feature details from API
 */
const fetchFeatureDetails = async (featureCode) => {
  if (!featureCode) return

  isLoadingFeatureDetails.value = true
  featureDetails.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${featureCode}`
    })

    if (response.data) {
      featureDetails.value = response.data

      // Cache heavy feature geometry in the store so the map renders it
      if (response.data.properties?.is_heavy && response.data.geometry) {
        store.commit('geo/SET_HEAVY_FEATURE_GEOMETRY', {
          code: featureCode,
          geometry: response.data.geometry
        })
      }

      // Fetch associated project if available
      if (response.data.properties?.project_id) {
        await fetchProjectData(response.data.properties.project_id)
      }
    }
  } catch (error) {
    console.error('Error loading feature details:', error)
  } finally {
    isLoadingFeatureDetails.value = false
  }
}

/**
 * Fetch project data for context
 */
const fetchProjectData = async (projectId) => {
  if (!projectId) return

  isLoadingProjectData.value = true
  projectData.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}`
    })

    if (response.data) {
      projectData.value = response.data
    }
  } catch (error) {
    console.error('Error loading project data:', error)
  } finally {
    isLoadingProjectData.value = false
  }
}

const closePopup = () => {
  emit('close')
}

/**
 * Navigate to the feature detail page
 */
const openFeaturePage = () => {
  if (!featureDetails.value?.properties) return

  const { project_id, code } = featureDetails.value.properties
  const org_id = projectData.value?.project?.organization_id

  if (!org_id || !project_id || !code) return

  router.push({
    name: 'Feature',
    params: {
      org_id,
      project_id,
      featureCode: code
    },
    query: {
      skipZoom: 'true'
    }
  })

  closePopup()
}

// Computed timezone
const timezone = computed(() => props.settings?.tz || 'UTC')

// Heavy feature state
const featureCode = computed(() => featureDetails.value?.properties?.code)
const isHeavy = computed(() => featureDetails.value?.properties?.is_heavy === true)
const isHeavyLoading = computed(() => featureCode.value ? store.getters['geo/isHeavyFeatureLoading'](featureCode.value) : false)
const isHeavyLoaded = computed(() => featureCode.value ? store.getters['geo/isHeavyFeatureLoaded'](featureCode.value) : false)

const loadHeavyGeometry = () => {
  if (!featureCode.value || isHeavyLoaded.value) return
  store.dispatch('geo/loadHeavyFeatureGeometry', featureCode.value)
}

// Watch for feature changes to refetch details
watch(() => [props.feature, props.counter], ([newFeature]) => {
  if (newFeature?.properties?.code) {
    fetchFeatureDetails(newFeature.properties.code)
  } else {
    featureDetails.value = null
    projectData.value = null
  }
}, { immediate: true })
</script>

<template>
  <MapLayerPopup
    v-if="isVisible && coordinates && map"
    :map="map"
    :coordinates="coordinates"
    :show="isVisible"
    :offset="10"
    :max-width="'300px'"
    :min-width="'300px'"
    @close="closePopup"
    :key="`feature-${feature?.id || code}-popup-${counter}`"
  >
    <div v-if="feature" class="p-2" style="min-width: 250px;">
      <!-- Loading state -->
      <div v-if="isLoadingFeatureDetails" class="text-sm text-gray-500">
        Loading detailed information...
      </div>

      <!-- Feature details loaded -->
      <div v-else-if="featureDetails">
        <div v-if="featureDetails.properties">
          <!-- Project context -->
          <div class="flex items-center -mt-1 mb-1.5 text-xs text-gray-500">
            <span v-if="isLoadingProjectData">Loading project...</span>
            <span v-else-if="projectData">{{ projectData.project.project_name }}</span>
            <span v-else>{{ featureDetails.properties.project_id || 'Unknown Project' }}</span>
          </div>

          <!-- Feature header with icon -->
          <div class="flex items-center mb-2">
            <div class="mr-2 w-8 h-8 rounded-full shadow-sm flex items-center justify-center bg-primary">
              <div v-if="featureDetails.properties?.s3_icon">
                <img class="object-contain w-5 h-5" :src="featureDetails.properties?.s3_icon" alt="">
              </div>
              <div v-else>
                <Point v-if="featureDetails.geometry?.type === 'Point'" class="fill-white" />
                <LineString v-if="featureDetails.geometry?.type === 'LineString'" class="fill-white" />
                <Polygon v-if="featureDetails.geometry?.type === 'Polygon'" class="fill-white" />
                <Polygon v-if="featureDetails.geometry?.type === 'GeometryCollection'" class="fill-white" />
                <MultiPolygon v-if="featureDetails.geometry?.type === 'MultiPolygon'" class="fill-white" />
              </div>
            </div>
            <div>
              <div class="font-semibold text-sm">{{ featureDetails.properties.name || 'Unnamed Feature' }}</div>
              <div class="text-xs text-gray-500">{{ featureDetails.properties.member_display_name || 'Unknown User' }}</div>
            </div>
          </div>

          <!-- Public badge -->
          <div v-if="featureDetails.properties.shared" class="mb-2">
            <Badge variant="secondary" class="text-xs">Public</Badge>
          </div>

          <!-- Description -->
          <div v-if="featureDetails.properties.description" class="text-sm my-2 text-gray-700">
            {{ featureDetails.properties.description }}
          </div>

          <!-- Metadata -->
          <ul class="text-xs text-gray-500 mb-3 space-y-1">
            <li class="flex items-center">
              <span class="font-medium">{{ formatTime(featureDetails.properties.created_at, timezone, 'D MMMM YYYY HH:mm') }}</span>
            </li>
            <li v-if="featureDetails.properties.updated_at" class="flex items-center">
              <span class="text-gray-500 mr-1">Updated:</span>
              <span class="font-medium">{{ formatTime(featureDetails.properties.updated_at, timezone, 'D MMMM YYYY HH:mm') }}</span>
            </li>
            <li v-if="hasAreaGeometry(featureDetails.geometry)" class="flex items-center">
              <span class="text-gray-500 mr-1">Area:</span>
              <span class="font-medium">{{ (area(featureDetails.geometry) / 10000).toFixed(4) }} ha ({{ (area(featureDetails.geometry) / 1000000).toFixed(4) }} km²)</span>
            </li>
          </ul>
        </div>

        <!-- Project data -->
        <div v-if="featureDetails.properties && featureDetails.properties.data" class="mb-3">
          <div class="text-xs font-medium mb-2 text-gray-600">Project Data</div>
          <div class="flex flex-wrap gap-1">
            <template v-if="typeof featureDetails.properties.data === 'object'">
              <span
                v-for="(item, index) in featureDetails.properties.data"
                :key="index"
                class="px-1.5 py-0.5 bg-secondary rounded"
              >
                {{ item.key }}: {{ item.value }}
              </span>
            </template>
            <span v-else class="px-1.5 py-0.5 bg-secondary rounded">
              {{ featureDetails.properties.data }}
            </span>
          </div>
        </div>

        <!-- Images gallery -->
        <div v-if="featureDetails.properties?.resources && featureDetails.properties.resources.length > 0" class="mb-3">
          <MediaList
            :resources="featureDetails.properties.resources"
            :show-delete="false"
          />
        </div>

        <!-- Additional attributes -->
        <div v-if="featureDetails.attributes && Object.keys(featureDetails.attributes).length > 0" class="bg-gray-50 p-3 rounded-md mb-3">
          <div class="text-xs font-medium mb-1 text-gray-600">Details</div>
          <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <template v-for="(value, key) in featureDetails.attributes" :key="key">
              <div class="text-gray-500 truncate">{{ key }}:</div>
              <div class="font-medium truncate">{{ value }}</div>
            </template>
          </div>
        </div>

        <!-- Open button -->
        <div class="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            @click="openFeaturePage"
          >
            Open
          </Button>

          <!-- Load geometry button for heavy features -->
          <Button
            v-if="isHeavy && !isHeavyLoaded"
            @click="loadHeavyGeometry"
            size="sm"
            variant="outline"
            class="border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900"
            :disabled="isHeavyLoading"
          >
            <Loader2 v-if="isHeavyLoading" class="h-3.5 w-3.5 mr-1 animate-spin" />
            <Download v-else class="h-3.5 w-3.5 mr-1" />
            {{ isHeavyLoading ? 'Loading...' : 'Load' }}
          </Button>
        </div>

        <!-- Comment thread -->
        <div
          v-if="featureDetails.properties.project_id"
          class="mt-3 border-t pt-2"
        >
          <FeatureThread
            :feature-code="featureDetails.properties.code"
            :project-id="featureDetails.properties.project_id"
            :key="featureDetails.properties.code"
          />
        </div>
      </div>

      <!-- Fallback: Show raw properties -->
      <div v-else-if="feature.properties" class="bg-gray-50 p-3 rounded-md">
        <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <template v-for="(value, key) in feature.properties" :key="key">
            <div class="text-gray-500 truncate">{{ key }}:</div>
            <div class="font-medium truncate">{{ value }}</div>
          </template>
        </div>
      </div>

      <!-- No data -->
      <div v-else class="text-sm text-gray-500">
        No properties available for this feature
      </div>
    </div>
  </MapLayerPopup>
</template>
